import { Phone, MapPin, CheckCircle, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const HackneyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Recovery Hackney | 24/7 Breakdown Service E5, E8, E9 | Vehicle Transport"
        description="Fast car recovery in Hackney E5, E8, E9. 30-minute response time. 24/7 breakdown service, vehicle transport, jump starts. Local Hackney service. Call 07492 639223 now."
        keywords="car recovery hackney, breakdown service hackney, car recovery e5, car recovery e8, car recovery e9, hackney vehicle transport, dalston car recovery, hackney central breakdown"
      />

      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              📍 Local Hackney Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Car Recovery & Vehicle Transport in Hackney
            </h1>
            <p className="text-xl text-slate-200 mb-8">
              Fast 30-minute car recovery across Hackney E5, E8, E9. Available 24/7 for breakdowns, vehicle transport, and emergency recovery in Hackney Central, Dalston, Homerton, and surrounding areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Car Recovery Services in Hackney</h2>
            <p className="text-lg text-slate-700 mb-6">
              Based locally in Hackney, Ovawell Autos provides the fastest car recovery and vehicle transport service across all Hackney postcodes. Our average 30-minute response time beats national services by 30-60 minutes because we're already in your area.
            </p>

            <h3 className="text-2xl font-bold mb-4 mt-8">Hackney Areas We Cover</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                { area: "Hackney Central", postcode: "E8" },
                { area: "Dalston", postcode: "E8" },
                { area: "London Fields", postcode: "E8" },
                { area: "Homerton", postcode: "E9" },
                { area: "Hackney Downs", postcode: "E5" },
                { area: "Clapton", postcode: "E5" },
                { area: "Hackney Wick", postcode: "E9" },
                { area: "Stoke Newington", postcode: "N16" },
                { area: "Victoria Park", postcode: "E9" }
              ].map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold">{item.area}</p>
                        <p className="text-sm text-slate-600">{item.postcode}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Our Hackney Services</h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">24/7 Emergency Breakdown Recovery</h4>
                  <p className="text-slate-600 mb-3">
                    Broken down on Mare Street, Kingsland Road, or anywhere in Hackney? We respond in 30 minutes average with emergency recovery across E5, E8, E9 postcodes.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Clock className="w-4 h-4" />
                    <span className="font-semibold">30-minute avg response in Hackney</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Vehicle Transportation Hackney</h4>
                  <p className="text-slate-600 mb-3">
                    Need to move a car within Hackney or from Hackney to East London? We transport running and non-running vehicles door-to-door across all Hackney areas.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-semibold">Same-day service available</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg mb-2">Jump Start Service Hackney</h4>
                  <p className="text-slate-600 mb-3">
                    Dead battery in a Dalston car park or Hackney Central? Our mobile jump start service reaches you in 30 minutes with professional equipment.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Clock className="w-4 h-4" />
                    <span className="font-semibold">Fast mobile service</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Why Choose Local Hackney Service?</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold mb-2">Faster Response Times</h4>
                  <p className="text-slate-600">
                    Based in Hackney, we reach you in 30 minutes average versus 60-90 minutes for national services dispatched from outside London.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold mb-2">Local Knowledge</h4>
                  <p className="text-slate-600">
                    We know every street in Hackney - from Mare Street traffic to Dalston one-way systems. This local expertise means faster, more efficient service.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold mb-2">Better Prices</h4>
                  <p className="text-slate-600">
                    Competitive local rates without national call center overhead. Fair pricing for Hackney residents: £60-£80 within Hackney.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold mb-2">Community Trust</h4>
                  <p className="text-slate-600">
                    We're part of the Hackney community. Check our Google reviews from real E5, E8, and E9 customers.
                  </p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Common Hackney Breakdown Locations</h3>
            <p className="text-slate-700 mb-4">
              We regularly provide recovery services at these Hackney locations:
            </p>
            <div className="grid md:grid-cols-2 gap-3 mb-8">
              {[
                "Mare Street (A107)",
                "Kingsland Road (A10)",
                "Morning Lane",
                "Well Street",
                "Dalston Lane",
                "Graham Road",
                "Hackney Road (A1208)",
                "Victoria Park Road",
                "Homerton High Street",
                "Chatsworth Road",
                "Clapton Common",
                "Hackney Central Station area"
              ].map((location, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded border">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="text-sm">{location}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Hackney Car Recovery Prices</h3>
            <Card className="mb-8">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between pb-3 border-b">
                    <span>Within Hackney (E5, E8, E9)</span>
                    <span className="font-bold text-lg">£60-£80</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b">
                    <span>Hackney to Shoreditch/Bethnal Green</span>
                    <span className="font-bold text-lg">£70-£90</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b">
                    <span>Hackney to Stratford/Islington</span>
                    <span className="font-bold text-lg">£80-£100</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Night/Weekend (11pm-7am)</span>
                    <span className="font-bold text-lg">+30-50%</span>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  All prices include recovery, transport and insurance. Transparent quotes before we start.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Broken Down in Hackney?</h2>
          <p className="text-xl mb-8">Fast 30-minute car recovery across E5, E8, E9. Call now for immediate help.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                Call: {companyInfo.phone}
              </Button>
            </a>
            <a href={`https://wa.me/${companyInfo.whatsappNumber}?text=I need car recovery in Hackney`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
